import ProductFormConfig from "@components/forms/ProductForm/form";
import { ProductFormData } from "@/formTypes/ProductFormDataType";
import { buildImportFieldsFromForm } from "./core/fieldsFromForm";
import { ImportField } from "./core/types";

/** Como hay que normalizar el valor crudo del archivo antes de guardarlo. */
export type ProductImportValueKind =
    | "text"
    | "code"
    | "boolean"
    | "integer"
    | "decimal"
    | "cabys"
    | "productType"
    | "taxTypeCode"
    | "taxPercentageCode"
    | "measureUnit"
    | "categoryName";

export type ProductImportField = ImportField & {
    name: (keyof ProductFormData & string) | "partidaArancelaria";
    kind: ProductImportValueKind;
};

/**
 * Atributos que no se ofrecen como destino de una columna: `imagen` es un archivo subido
 * al bucket, no un valor de texto que se pueda leer de un CSV.
 */
const EXCLUDED_FIELDS = new Set<string>(["imagen"]);

/**
 * Etiquetas propias donde la del formulario no describe bien lo que se va a importar.
 * Las que dicen "predeterminado" vienen de la pantalla de categorias, donde el valor si es
 * un default; en un producto es el valor real.
 */
const LABEL_OVERRIDES: Record<string, string> = {
    categoryId: "Categoria (por nombre)",
    descripcion_fact: "Descripcion de Factura (nombre del producto)",
    descripcion: "Descripcion Larga",
    tipo_producto: "Tipo de Producto (Mercancia/Servicio)",
    tipo_impuesto: "Tipo de Impuesto",
    porcentaje_impuesto: "Porcentaje de Impuesto",
    unidad_medida: "Unidad de Medida",
};

const FIELD_KINDS: Record<string, ProductImportValueKind> = {
    codigo: "code",
    codigo_cabys: "cabys",
    codigobarras: "code",
    descripcion_fact: "text",
    descripcion: "text",
    categoryId: "categoryName",
    costo: "decimal",
    existencias: "decimal",
    minimo: "decimal",
    maximo: "decimal",
    precio1: "decimal",
    precio2: "decimal",
    precio3: "decimal",
    precio4: "decimal",
    comision: "decimal",
    visibilidad: "integer",
    tipo_producto: "productType",
    tipo_impuesto: "taxTypeCode",
    porcentaje_impuesto: "taxPercentageCode",
    unidad_medida: "measureUnit",
    exento: "boolean",
    activo: "boolean",
};

/** Atributos del modelo `Product` que no aparecen en el formulario pero si se pueden importar. */
const EXTRA_FIELDS: ProductImportField[] = [
    { name: "comision", label: "Comision", kind: "decimal" },
    { name: "partidaArancelaria", label: "Partida Arancelaria", kind: "text" },
];

export const buildProductImportFields = (): ProductImportField[] =>
    buildImportFieldsFromForm(ProductFormConfig, {
        excluded: EXCLUDED_FIELDS,
        labelOverrides: LABEL_OVERRIDES,
        kinds: FIELD_KINDS,
        extraFields: EXTRA_FIELDS,
    }) as ProductImportField[];

export const PRODUCT_IMPORT_FIELDS: ProductImportField[] = buildProductImportFields();

export const findProductImportField = (name?: string | null): ProductImportField | undefined =>
    PRODUCT_IMPORT_FIELDS.find((field) => field.name === name);

/**
 * Lo unico obligatorio es el nombre del producto, y solo cuando no hay categoria mapeada:
 * el resto tiene default (ver `applyProductDefaults`) o se autogenera. La validacion fina
 * de cada fila la hace `resolveProductRows`.
 */
export const REQUIRED_PRODUCT_IMPORT_FIELDS: string[] = [];
